import React, { useState } from 'react';

interface StarRatingProps {
  value: number;
  onChange?: (value: number) => void;
  size?: 'sm' | 'md';
}

const FILLED_COLOR = '#f5a623';
const EMPTY_COLOR = '#d0d0d0';

type StarFill = 'full' | 'half' | 'empty';

const roundToHalf = (value: number): number => Math.round(value * 2) / 2;

const getStarFill = (value: number, star: number): StarFill => {
  if (value >= star) return 'full';
  if (value >= star - 0.5) return 'half';
  return 'empty';
};

const StarIcon: React.FC<{ fill: StarFill; fontSize: string }> = ({ fill, fontSize }) => {
  const baseStyle: React.CSSProperties = { fontSize, lineHeight: 1 };

  if (fill === 'full') {
    return <span style={{ ...baseStyle, color: FILLED_COLOR }}>★</span>;
  }

  if (fill === 'empty') {
    return <span style={{ ...baseStyle, color: EMPTY_COLOR }}>☆</span>;
  }

  return (
    <span style={{ ...baseStyle, position: 'relative', display: 'inline-block' }}>
      <span style={{ color: EMPTY_COLOR }}>☆</span>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '50%',
          overflow: 'hidden',
          color: FILLED_COLOR,
        }}
      >
        ★
      </span>
    </span>
  );
};

export const StarRating: React.FC<StarRatingProps> = ({ value, onChange, size = 'md' }) => {
  const [hover, setHover] = useState<number | null>(null);
  const displayValue = roundToHalf(hover ?? value);
  const fontSize = size === 'sm' ? '0.9rem' : '1.1rem';

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(star => {
        const fill = getStarFill(displayValue, star);
        const commonProps = {
          key: star,
          style: {
            cursor: onChange ? 'pointer' as const : 'default' as const,
            fontSize,
            padding: 0,
            border: 'none',
            background: 'transparent',
          },
        };

        if (!onChange) {
          return (
            <span {...commonProps}>
              <StarIcon fill={fill} fontSize={fontSize} />
            </span>
          );
        }

        return (
          <button
            type="button"
            {...commonProps}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(null)}
            onClick={() => onChange(star)}
          >
            <StarIcon fill={fill} fontSize={fontSize} />
          </button>
        );
      })}
    </div>
  );
};
